/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {inject, NgModule, provideAppInitializer} from '@angular/core';
import {OAuthModule} from 'angular-oauth2-oidc';
import {UserService} from '../../shared/user.service';
import {AuthenticationService} from './authentication.service';

/**
 * App initializer that starts the authentication flow
 *
 * This is the main part of where authentication starts.
 *
 * @param authenticationService - Auth service instance
 * @param userService - User service instance.
 * @returns initializer function
 */
export function initAuth(authenticationService: AuthenticationService, userService: UserService): () => void {
    /*
     * Returns a FUNCTION that returns a PROMISE.
     *
     * This function is invoked by angular during the bootstrap process.
     * When the Promise is resolved, the application will finish starting up.
     * We chain a total of 3 promises here, Each one must resolve before the next promise can start.
     *
     * The fourth step just assigns the resulting User object that was returned through the promise to the
     * service. This should already be done, but doing so here guarantees that the user is setup in order.
     */
    return () => doAuth(authenticationService, userService)
        .then(checkToken)
        .then(userService.createUser)
        .then((user) => {
            userService.user = user;
        });

}

/**
 * Step 2: Check to make sure that we've saved the token.
 *
 * This step (or the second promise in authenticationService.initialize()) may be unnecessary,
 * but doing this here will guarantee that we don't create the user
 * (by resolving this promise, starting the .next() call)
 * too early, before the token has been saved. If that happens, then the interceptor won't inject the JWT, and the
 * backend call will fail with 403, never creating the user, and never resolving the auth flow.
 */
function checkToken() {
    return new Promise<boolean>((resolver) => {
        // subscribe to our BehaviorSubject in auth service.
        // If the token's been saved already, we'll immediately return true and resolve.
        // If not, we'll resolve this as soon as it's saved in the callback from tokenSubscription().
        AuthenticationService.tokenSaved.subscribe((saved) => {
            if (saved) {
                resolver(true);
            }
        });
    });
}

/**
 * Step 1: Start the authentication flow, and resolve a Promise when we've successfully authed.
 * @param authenticationService
 * @param userService
 */
function doAuth(authenticationService: AuthenticationService, userService: UserService) {
    return new Promise<any>((resolve) => {
        // Start OAuth. Resolves to isAuthenticated if we've authed properly and saved the token.
        authenticationService.initialize()
            .then((isAuthenticated) => {
                if (isAuthenticated) {
                    // If we've been authenticated and saved the token, then resolve this promise.
                    // Resolving will let the .next() call on this promise run.
                    resolve(isAuthenticated);
                } else {
                    // TODO: Handle non-authenticated cases.
                    // Currently if auth fails, CP will infinitely wait for auth because the promise here
                    // is never resolved, so we'll never create the user and finish loading.
                }
            });
    });
}

/**
 * Bind this module to the APP_INITIALIZER
 * We want this service to startup with our application.
 * Angular will wait for all promises that provide to APP_INITIALIZER before finishing the
 * bootstrap process.
 * This will force our authentication to be one of the first things that happen, and the app will
 * fail to continue loading if this module does not successfully resolve.
 *
 * We want to use the initAuth method as the entrypoint for this module.
 */
@NgModule({
    imports: [OAuthModule.forRoot()],
    providers: [
        AuthenticationService,
        UserService,
        provideAppInitializer(() => {
        const initializerFn = (initAuth)(inject(AuthenticationService), inject(UserService));
        return initializerFn();
      }),
    ],
})
export class AuthenticationModule {
}
